import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import {
    NotificationType,
    Guid,
    Notification
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    forLifeOf,
    getDynamicRouteLink,
    getRouterLinkUrlById,
    LifecycleAware
} from '@lsq-frontend/common-ui';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import {
    atlas,
    INVOICES_INVOICE,
    REPORTS_FUNDINGS,
    REPORTS_PURCHASES
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { SurveyService } from '../services/survey.service';
import { NotificationsService } from './notifications.service';

@LifecycleAware()
@Component({
    selector: 'notifications',
    template: `
        <div class="notifications-animation-wrapper">
            <ng-container *ngIf="notificationsService.isExpanded$ | async">
                <div
                    class="notification-window"
                    [ngClass]="{ 'is-empty': !(notificationsCount$ | async) }"
                    (clickOutside)="closeWindow()"
                    [exclude]="'#notification-nav-item-mobile, #notification-nav-item'"
                    [@slide]
                >
                    <div *ngIf="!(notificationsCount$ | async)" class="notification-window-empty">
                        <span>No New Notifications</span>
                    </div>
                    <div *ngIf="notificationsCount$ | async">
                        <div class="notification-window-title">
                            <span>Notifications</span>
                            <a
                                *ngIf="nonPersistentNotificationsCount$ | async"
                                (click)="handleAcknowledgeAllNotifications()"
                            >
                                Clear All
                            </a>
                        </div>
                        <ng-scrollbar #scrollBarRef>
                            <div class="notification-window-notifications">
                                <ng-container *ngIf="!isRefreshingAfterAcknowledgement">
                                    <div
                                        *ngFor="
                                            let notification of notifications$ | async;
                                            trackBy: trackNotifications
                                        "
                                        [@collapse]
                                        class="notification"
                                    >
                                        <notification
                                            [notification]="notification"
                                            (notificationAction)="handleNotificationAction($event)"
                                            (closeNotification)="
                                                handleAcknowledgeOneNotification($event)
                                            "
                                        >
                                        </notification>
                                    </div>
                                </ng-container>
                                <ng-container *ngIf="isRefreshingAfterAcknowledgement">
                                    <lsq-circle-loader></lsq-circle-loader>
                                </ng-container>
                            </div>
                        </ng-scrollbar>
                    </div>
                </div>
            </ng-container>
        </div>
    `,
    styleUrls: ['./notifications.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('slide', [
            transition(':enter', [
                style({
                    transform: 'translateY(-100%)'
                }),
                animate(
                    '200ms cubic-bezier(0, 0.5, 0.5, 1)',
                    style({
                        transform: 'translateY(0)'
                    })
                )
            ]),
            transition(':leave', [
                animate(
                    '200ms cubic-bezier(0, 0.5, 0.5, 1)',
                    style({
                        transform: 'translateY(-100%)'
                    })
                )
            ])
        ]),
        trigger('collapse', [
            transition(':leave', [
                animate(
                    '200ms cubic-bezier(0, 0.5, 0.5, 1)',
                    style({
                        height: '0'
                    })
                )
            ])
        ])
    ]
})
export class NotificationsComponent implements OnDestroy {
    public notifications$: Observable<Notification[]>;
    public notificationsCount$: Observable<number>;
    public nonPersistentNotificationsCount$: Observable<number>;
    public isRefreshingAfterAcknowledgement = false;

    constructor(
        private _surveyService: SurveyService,
        public notificationsService: NotificationsService,
        public router: Router
    ) {
        this.notificationsService.toggleNotificationFetching(true);

        this.notifications$ = this.notificationsService.notifications$.pipe(
            forLifeOf(this),
            // Put the persistent notifications at the top.
            map((notifications) => {
                const notificationCopy = [...notifications];
                return notificationCopy.sort(({ isPersistent }) => (isPersistent ? -1 : 1));
            })
        );

        this.notificationsCount$ = this.notificationsService.notificationCount$;

        this.nonPersistentNotificationsCount$ = this.notifications$.pipe(
            map(
                (notifications) =>
                    notifications.filter((notification) => !notification.isPersistent).length
            )
        );

        this.notificationsService.notifications$.subscribe(() => {
            this.isRefreshingAfterAcknowledgement = false;
        });
    }

    public ngAfterViewInit(): void {
        this.notificationsService.refreshNotifications();
    }

    public ngOnDestroy(): void {
        this.notificationsService.toggleNotificationFetching(false);
    }

    public closeWindow(): void {
        this.notificationsService.hide();
    }

    public async handleNotificationAction(notification: Notification): Promise<void> {
        this.notificationsService.hide();

        // In the future, add more actions based on notification type.
        // For example, FundsReleased type could navigate to the funding report
        // (Not doing this now becasue fundings report is 5 min delayed, but notifications are real time)
        switch (notification.type) {
            case NotificationType.Survey:
                this._surveyService.startSurvey(notification.entityKey);
                break;
            case NotificationType.InvoicesProcessed:
                await this.router.navigateByUrl(getRouterLinkUrlById(atlas, REPORTS_PURCHASES));
                this.handleAcknowledgeOneNotification(notification);
                break;
            case NotificationType.FundsReleased:
                await this.router.navigateByUrl(getRouterLinkUrlById(atlas, REPORTS_FUNDINGS));
                this.handleAcknowledgeOneNotification(notification);
                break;
            case NotificationType.InvoiceDownload:
                // TODO: Invoice Details page does not yet support GUIDs.
                // https://app.clubhouse.io/lsq/story/13262/update-invoice-details-page-to-support-a-guid-key
                const nonGuidInvoiceKey = notification.entityKey.split('-').join('');
                await this.router.navigateByUrl(
                    getDynamicRouteLink(getRouterLinkUrlById(atlas, INVOICES_INVOICE), {
                        invoiceKey: nonGuidInvoiceKey
                    })
                );
                this.handleAcknowledgeOneNotification(notification);
                break;
            default:
                // Do nothing.
                return;
        }
    }

    public trackNotifications(_index: number, notification: Notification): Guid {
        return notification.notificationKey;
    }

    public async handleAcknowledgeAllNotifications(): Promise<void> {
        this.isRefreshingAfterAcknowledgement = true;
        const notifications = await this.notificationsService.notifications$
            .pipe(first())
            .toPromise();
        await this.notificationsService.acknowledgeNotifications(notifications);
    }

    public async handleAcknowledgeOneNotification(notification: Notification): Promise<void> {
        this.isRefreshingAfterAcknowledgement = true;
        await this.notificationsService.acknowledgeNotifications([notification]);
    }
}
